import React, { useState, useCallback } from 'react'
import { get, patch } from 'aws-amplify/api'

import AppointmentsContext from '../context/AppointmentsContext'
import errorTrackingService from '../services/errorTrackingService'
import { buildPatientsApiUrl } from '../utils/patientsApiBaseUrl'

const AppointmentsProvider = ({ children }) => {
  const [currentView, setCurrentView] = useState('Summary')

  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const getAppointments = async (tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: '/booking/getAppointments',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/booking/getAppointments', 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/booking/getAppointments', 'GET')
        console.log(error)
        return undefined
      }
    }
  }

  const cancelAppointment = useCallback(async (appointmentId, tokenAuth) => {
    try {
      const resp = patch({
        apiName: baseUrl,
        path: `/api/v2/booking/appointments/${appointmentId}/cancellation`,
        options: {
          headers: {
            Authorization: `Bearer ${tokenAuth.__raw}`,
            'Request-Channel': 'app-web'
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      // Check for success: false in 200 response
      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, `/api/v2/booking/appointments/${appointmentId}/cancellation`, 'PATCH')
        throw new Error('Network Error')
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, `/api/v2/booking/appointments/${appointmentId}/cancellation`, 'PATCH')
      throw new Error('Network Error')
    }
  }, [baseUrl])

  const getAppointmentInfo = useCallback(async (appointment, tokenAuth) => {
    if (!tokenAuth) {
      throw new Error('Missing auth token for appointment checkup')
    }

    const endpoint = buildPatientsApiUrl(`/v4/appointments/${appointment}/checkup`)
    if (!endpoint) {
      console.error(
        'Missing REACT_APP_PATIENTS_API_BASE_URL for appointment checkup'
      )
      throw new Error('Missing REACT_APP_PATIENTS_API_BASE_URL for appointment checkup')
    }

    try {
      const response = await fetch(endpoint, {
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${tokenAuth.__raw}`
        }
      })
      const data = await response.json()

      if (!response.ok || (data.code !== undefined && data.code !== 0)) {
        const error = new Error(
          data?.message || 'Error al obtener el detalle de la cita'
        )
        error.isAPIError = true
        error.response = { status: response.status, data }
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        throw error
      }

      return data.data
    } catch (error) {
      if (!error.isAPIError) {
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        console.log(error)
      }
      throw error
    }
  }, [])

  const getResultsInfo = async (appointmentId, laboratoryId, tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: `/health/getLaboratoryResults?appointment_id=${appointmentId}&laboratory_id=${laboratoryId}`,
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        // Check for success: false in 200 response
        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/health/getLaboratoryResults', 'GET')
          return undefined
        }

        return data.body
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/health/getLaboratoryResults', 'GET')
        console.log(error)
        return undefined
      }
    }
  }

  return (
    <AppointmentsContext.Provider
      value={{
        getAppointments,
        currentView,
        setCurrentView,
        getAppointmentInfo,
        getResultsInfo,
        cancelAppointment
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  )
}

export default AppointmentsProvider
